import React, { useState } from "react";
import useOverlayDismiss from "./useOverlayDismiss";

/**
 * Cuadro de notas del socio, abierto desde su ficha (ClientDetailModal).
 * Mismo contrato que el panel de Entradas: guarda nota + aviso "abrir al
 * interactuar" en el socio (PUT /v1/clients), compartido con todo el equipo.
 * Solo envía los campos QUE CAMBIARON: tocar el toggle nunca reescribe la
 * nota (evita pisar la de un compañero desde un snapshot viejo).
 * Acepta el shape de API (profile.*) o el mapeado con campos a nivel raíz.
 */
export default function NotasSocioModal({ socio, token, onClose, onSaved }) {
  const p = socio?.profile || {};
  const notaInicial = String(p.notes ?? socio?.notes ?? "");
  const popupInicial = !!(p.notes_popup ?? socio?.notes_popup);
  const [draft, setDraft] = useState(notaInicial);
  const [popupOn, setPopupOn] = useState(popupInicial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Cerrar solo si TODO el gesto (mousedown+mouseup+click) fue en el fondo:
  // arrastrar una selección de texto fuera del textarea ya no cierra el modal.
  const overlay = useOverlayDismiss(onClose);
  if (!socio) return null;

  const nombre =
    `${p.first_name || ""} ${p.last_name || ""}`.trim() || socio?.name || socio?.email || "Socio";

  const persistir = async (cambios) => {
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("client_data", JSON.stringify({ profile: cambios }));
      const res = await fetch(`/v1/clients/${socio.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "No se pudieron guardar las notas");
      }
      onSaved?.(cambios);
      onClose?.();
    } catch (e) {
      setError(e?.message || "Error al guardar las notas");
    } finally {
      setSaving(false);
    }
  };

  const guardar = () => {
    if (saving) return;
    const cambios = {};
    if (draft.trim() !== notaInicial.trim()) cambios.notes = draft.trim() || null;
    if (popupOn !== popupInicial) cambios.notes_popup = popupOn;
    if (Object.keys(cambios).length === 0) {
      onClose?.();
      return;
    }
    return persistir(cambios);
  };

  // Igual que el "Borrar notas" del panel de Entradas: deja la nota a null.
  // El aviso deja de saltar solo (sin nota no hay popup), el flag no se toca.
  const borrar = () => {
    if (saving) return;
    setDraft("");
    return persistir({ notes: null });
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      {...overlay}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
          Cuadro de notas
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{nombre}</h3>

        <label className="mt-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700">
          <button
            type="button"
            onClick={() => setPopupOn((v) => !v)}
            className={`relative h-7 w-12 rounded-full transition ${popupOn ? "bg-lime-500" : "bg-slate-300"}`}
            aria-pressed={popupOn}
          >
            <span
              className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${popupOn ? "left-6" : "left-1"}`}
            />
          </button>
          <span>
            Abrir al interactuar con el socio
            <span className="block text-xs text-gray-400">
              Se guarda para este socio y lo ve todo el equipo
            </span>
          </span>
        </label>

        {error ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escribe notas internas sobre este socio…"
          className="mt-3 h-40 w-full resize-none rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-900"
        />

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={borrar}
            disabled={saving || (!notaInicial.trim() && !draft.trim())}
            className="flex-1 rounded-xl border border-red-200 bg-white px-3 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Borrar notas
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={saving}
            className="flex-1 rounded-xl bg-gray-900 px-3 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar notas"}
          </button>
        </div>
      </div>
    </div>
  );
}
